import React, { useEffect, useRef, useState } from "react";
import VirbrixliteTagPill from "./VirbrixliteTagPill";
import {
  createEmptyTab,
  normalizeImages,
  normalizeTags,
  parseTabs,
} from "./virbrixliteConfigUtils";

const getEnvironmentImageUrl = (url) => {
  if (!url) return "";

  try {
    const resolvedUrl = new URL(url, window.location.origin);
    if (!["http:", "https:"].includes(resolvedUrl.protocol)) {
      return url;
    }

    const isLocal =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    const origin = isLocal
      ? `${window.location.protocol}//${window.location.hostname}:5000`
      : window.location.origin;

    return `${origin}${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
  } catch (error) {
    return url;
  }
};

const getImagePath = (url) => {
  if (!url) return "";

  try {
    const resolvedUrl = new URL(url, window.location.origin);
    return `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
  } catch (error) {
    return url;
  }
};

const VirbrixliteConfig = ({ value, onChange }) => {
  const [liteTabs, setLiteTabs] = useState([]);
  const [editingTab, setEditingTab] = useState(null);
  const [showTabForm, setShowTabForm] = useState(false);
  const [draggedTabIndex, setDraggedTabIndex] = useState(null);
  const [dragOverTabIndex, setDragOverTabIndex] = useState(null);
  const [formError, setFormError] = useState("");
  const [tabFormData, setTabFormData] = useState(createEmptyTab);
  const skipChangeRef = useRef(false);

  useEffect(() => {
    skipChangeRef.current = true;
    setLiteTabs(parseTabs(value));
  }, [value]);

  useEffect(() => {
    if (skipChangeRef.current) {
      skipChangeRef.current = false;
      return;
    }
    onChange(liteTabs);
  }, [liteTabs, onChange]);

  const closeTabForm = () => {
    setShowTabForm(false);
    setEditingTab(null);
    setFormError("");
    setTabFormData(createEmptyTab());
  };

  const handleAddTab = () => {
    setEditingTab(null);
    setFormError("");
    setTabFormData(createEmptyTab());
    setShowTabForm(true);
  };

  const handleEditTab = (tab) => {
    setEditingTab(tab.id);
    setFormError("");
    setTabFormData({
      ...tab,
      enabled: tab.enabled !== false,
      images: normalizeImages(tab),
    });
    setShowTabForm(true);
  };

  const handleSaveTab = () => {
    if (!tabFormData.name.trim() || !tabFormData.display.trim() || !tabFormData.src.trim()) {
      setFormError("Name, display, and URL are required.");
      return;
    }
    if (
      tabFormData.images.some(
        (image) => !image.data && !image.url,
      )
    ) {
      setFormError("Select an image file for every image entry.");
      return;
    }
    if (
      tabFormData.images.some(
        (image) => !image.description.trim() || !image.tag.trim(),
      )
    ) {
      setFormError("Add a short description and tag for every image.");
      return;
    }

    const newTab = {
      ...tabFormData,
      id: editingTab || tabFormData.id || Date.now(),
    };
    setLiteTabs((tabs) =>
      editingTab
        ? tabs.map((tab) => (tab.id === editingTab ? newTab : tab))
        : [...tabs, newTab],
    );
    closeTabForm();
  };

  const updateImage = (imageId, changes) => {
    setTabFormData((tab) => ({
      ...tab,
      images: tab.images.map((image) =>
        image.id === imageId ? { ...image, ...changes } : image,
      ),
    }));
  };

  const handleImageFile = (imageId, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setFormError("Only image files can be uploaded.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () =>
      updateImage(imageId, {
        name: file.name,
        data: reader.result,
        url: "",
      });
    reader.onerror = () => setFormError(`Unable to read ${file.name}.`);
    reader.readAsDataURL(file);
  };

  const addImage = () => {
    setTabFormData((tab) => ({
      ...tab,
      images: [
        ...tab.images,
        {
          id: `${tab.id}-image-${Date.now()}`,
          name: "",
          data: "",
          url: "",
          description: "",
          tag: "",
        },
      ],
    }));
  };

  const removeImage = (imageId) => {
    setTabFormData((tab) => ({
      ...tab,
      images: tab.images.filter((image) => image.id !== imageId),
    }));
  };

  const reorderTabs = (fromIndex, toIndex) => {
    if (fromIndex === null || toIndex === null || fromIndex === toIndex) return;
    setLiteTabs((tabs) => {
      const updated = [...tabs];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  };

  return (
    <div className="card w-100 p-2 p-sm-3 p-lg-4 shadow-lg border admin-list mb-2 h-max-content">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="text-main fw-bold m-0">Dashboard Links</h5>
        <button type="button" className="btn button-parent admin-list-btn-primary text-main" onClick={handleAddTab}>
          + Add
        </button>
      </div>

      {showTabForm && (
        <div className="lite-drawer-backdrop" role="presentation" onMouseDown={(e) => {
          if (e.target === e.currentTarget) closeTabForm();
        }}>
          <aside className="lite-drawer" role="dialog" aria-modal="true" aria-labelledby="virbrixlite-link-editor-title">
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div>
                <h6 id="virbrixlite-link-editor-title" className="text-main fw-bold mb-1">
                  {editingTab ? "Edit Dashboard Link" : "Add Dashboard Link"}
                </h6>
                <div className="text-muted small">Changes stay draft until you save the floor.</div>
              </div>
              <button type="button" className="lite-icon-btn" aria-label="Close editor" onClick={closeTabForm}>×</button>
            </div>
            <div className="lite-drawer-section">
              <div className="text-uppercase small text-muted fw-bold mb-3">Dashboard details</div>
              <div className="row">
                <div className="col-lg-4 mb-2">
                  <label className="form-label text-main">Name</label>
                  <input className="form-control rounded admin-list text-main" value={tabFormData.name}
                    onChange={(e) => setTabFormData({ ...tabFormData, name: e.target.value })} />
                </div>
                <div className="col-lg-4 mb-2">
                  <label className="form-label text-main">Display</label>
                  <input className="form-control rounded admin-list text-main" value={tabFormData.display}
                    onChange={(e) => setTabFormData({ ...tabFormData, display: e.target.value })} />
                </div>
                <div className="col-lg-4 mb-2">
                  <label className="form-label text-main">URL</label>
                  <input className="form-control rounded admin-list text-main" value={tabFormData.src}
                    onChange={(e) => setTabFormData({ ...tabFormData, src: e.target.value })} />
                </div>
              </div>
                    </div>

            <div className="lite-drawer-section mt-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="text-main fw-bold mb-0">Dashboard Images</h6>
                <button type="button" className="btn admin-list-btn-primary text-main btn-sm" onClick={addImage}>
                  + Add Image
                </button>
              </div>
              <p className="text-muted small mb-3">
                Add optional images that will be shown with this dashboard. Add a short description first, then a tag for filtering or display.
              </p>
              {tabFormData.images.length === 0 && (
                <p className="text-muted mb-0">No images configured for this dashboard.</p>
              )}
              {tabFormData.images.map((image, index) => (
                <div
                  key={image.id}
                  className="card admin-list lite-image-row mb-2">
                  <div className="row align-items-center g-2">
                    <div className="col-auto">
                      {image.data || image.url ? (
                        <img className="lite-image-preview" src={image.data || getEnvironmentImageUrl(image.url)} alt={image.description || image.tag} />
                      ) : (
                        <div className="lite-image-preview d-flex align-items-center justify-content-center text-muted">Image</div>
                      )}
                    </div>
                    <div className="col">
                      <label className="form-label text-main fw-bold small mb-1">Uploaded image</label>
                      <input type="file" accept="image/*" className="form-control form-control-sm rounded admin-list text-main mb-2"
                        onChange={(e) => handleImageFile(image.id, e.target.files[0])} />
                      <label className="form-label text-main fw-bold small mb-1">Image description</label>
                      <input className="form-control form-control-sm rounded admin-list text-main mb-2" placeholder="e.g. Main lobby"
                        value={image.description} onChange={(e) => updateImage(image.id, { description: e.target.value })} />
                      <label className="form-label text-main fw-bold small mb-1">Image tags</label>
                      <input className="form-control form-control-sm rounded admin-list text-main" placeholder="e.g. entrance"
                        value={image.tag} onChange={(e) => updateImage(image.id, { tag: e.target.value })} />
                      <div className="text-muted small mt-1">Separate multiple tags with commas.</div>
                      {normalizeTags(image.tag).length > 0 && (
                        <div className="d-flex flex-wrap gap-1 mt-2">
                          {normalizeTags(image.tag).map((tag) => (
                            <VirbrixliteTagPill key={tag} tag={tag} />
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="col-auto">
                      <button type="button" className="lite-icon-btn lite-delete" aria-label="Remove image" onClick={() => removeImage(image.id)}>×</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {formError && <div className="alert alert-danger mt-3 mb-0">{formError}</div>}
            <div className="d-flex gap-2 mt-3">
              <button type="button" className="btn admin-list-btn-secondary text-main" onClick={handleSaveTab}>Done</button>
              <button type="button" className="btn lite-icon-btn" onClick={closeTabForm}>Cancel</button>
            </div>
          </aside>
        </div>
      )}

      {liteTabs.length === 0 ? (
        <p className="text-muted mb-0">No dashboard links configured yet.</p>
      ) : (
        <div className="row g-3">
          {liteTabs.map((tab, index) => (
            <div key={tab.id} className="col-12">
              <div
                className={`card p-3 admin-list lite-drag ${draggedTabIndex === index ? "dragging" : ""} ${dragOverTabIndex === index ? "drag-over" : ""}`}
                draggable
                onDragStart={() => setDraggedTabIndex(index)}
                onDragOver={(e) => { e.preventDefault(); setDragOverTabIndex(index); }}
                onDrop={(e) => { e.preventDefault(); reorderTabs(draggedTabIndex, index); setDraggedTabIndex(null); setDragOverTabIndex(null); }}
                onDragEnd={() => { setDraggedTabIndex(null); setDragOverTabIndex(null); }}
              >
                <div className="d-flex align-items-start justify-content-between gap-3">
                  <div className="d-flex gap-2">
                    <span className="text-muted" title="Drag to reorder">::</span>
                    <div className="lite-summary-card">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <h6 className="text-main fw-bold mb-0">{tab.display || tab.name}</h6>
                      </div>
                      <div className="text-main small"><strong>Name:</strong> {tab.name}</div>
                      {tab.src && 
                                  (<div className="lite-image-url mt-1">
                                   <strong>URL:</strong>{" "}
                                    <a href={tab.src} target="_blank" rel="noreferrer">
                                      {tab.src}
                                    </a>
                                  </div>)}
                      {normalizeImages(tab).length > 0 && (
                        <div className="mt-4 pt-3 border-top">
                          <div className="text-main small fw-bold mb-2">Attached images:</div>
                          <div className="d-flex flex-column gap-2">
                            {normalizeImages(tab).map((image) => (
                              <div key={image.id} className="lite-attached-card small">
                                <div className="text-main">
                                  <strong>Description:</strong>{" "}
                                  {image.description || "Untitled image"}
                                </div>
                                <div className="d-flex flex-wrap align-items-center gap-1 mt-1">
                                  <strong className="text-main small">Tags:</strong>
                                  {normalizeTags(image.tag).length > 0 ? (
                                    normalizeTags(image.tag).map((tag) => (
                                      <VirbrixliteTagPill key={tag} tag={tag} />
                                    ))
                                  ) : (
                                    <span className="text-muted small">No tags</span>
                                  )}
                                </div>
                                {image.url && (
                                  <div className="lite-image-url mt-1">
                                    <strong>URL:</strong>{" "}
                                    <a href={getEnvironmentImageUrl(image.url)} target="_blank" rel="noreferrer">
                                      {getImagePath(image.url)}
                                    </a>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button type="button" className="lite-icon-btn" aria-label="Edit link" title="Edit link" onClick={() => handleEditTab(tab)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 17.46v3.04c0 .28.22.5.5.5h3.04c.13 0 .26-.05.35-.15L17.81 9.94l-3.75-3.75L3.15 17.1c-.1.1-.15.22-.15.36zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                      </svg>
                    </button>
                    <button type="button" className="lite-icon-btn lite-delete" aria-label="Delete link" title="Delete link"
                      onClick={() => setLiteTabs((tabs) => tabs.filter((item) => item.id !== tab.id))}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM8 9h8v10H8V9zm7.5-5-1-1h-5l-1 1H5v2h14V4z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VirbrixliteConfig;
